import { Box } from '@customink/pigment-react';
import type { DialogProps } from '@customink/pigment-react/lib/components/Dialog';
import {
  Dialog,
  DialogContent,
} from '@customink/pigment-react/lib/components/Dialog';
import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';

import ButtonSet from '@/components/ButtonSet';
import type { ButtonActions } from '@/components/ButtonSet';

import '@/components/Modal.css';

// https://mui.com/material-ui/react-modal/#performance
export default function Modal({
  buttonActions,
  centeredButtons,
  children,
  fullScreen,
  open,
  scroll,
  style,
  title,
  onClose,
  disableMobileStyles,
}: {
  buttonActions?: ButtonActions;
  centeredButtons?: boolean;
  children: ReactNode;
  fullScreen?: boolean;
  open: boolean;
  scroll?: DialogProps['scroll'];
  style?: CSSProperties;
  title: ReactNode;
  onClose: MouseEventHandler<HTMLButtonElement>;
  disableMobileStyles?: boolean;
}) {
  const modalSx = {
    '& .MuiDialogTitle-root': {
      fontSize: '1rem',
      lineHeight: '1.25',
      padding: '0 0.25rem 0 1rem',
      borderBottom: '1px solid #E3E3E3',
    },
    '& .MuiPaper-root': {
      ...style,
    },
    '& .MuiDialog-paperFullScreen': {
      borderRadius: '1rem',
    },
    ...(fullScreen ? { padding: '80px 25px' } : {}),
  };
  const dialogContentSx = {
    display: 'flex',
    ...(fullScreen ? { padding: '0' } : {}),
  };

  if (!open) return null;

  // Lazy load modal contents.
  return (
    <Dialog
      data-testid="modal"
      desktopVariant={fullScreen ? 'fullScreen' : 'default'}
      keepMounted={true}
      mobileVariant={fullScreen ? 'fullScreen' : 'default'}
      onClose={onClose}
      open={open}
      scroll={scroll}
      sx={modalSx}
      title={title}
    >
      <DialogContent sx={dialogContentSx}>
        <Box
          sx={{
            '& .MuiInputBase-input': {
              // Suppress layout thrashing when TextField is used with Dialog.
              // https://web.dev/avoid-large-complex-layouts-and-layout-thrashing/
              animation: 'none',
            },
            flexGrow: '1',
          }}
        >
          {children}
        </Box>
      </DialogContent>
      {!!buttonActions && (
        <ButtonSet
          actions={buttonActions}
          alignButtons="right"
          centered={centeredButtons}
          disableMobileStyles={disableMobileStyles}
        />
      )}
    </Dialog>
  );
}
